import { create } from 'zustand';

/**
 * Save state published by the active object form for the object-level toolbar.
 *
 * The toolbar renders in the page header — *above* `ControlledTabs` and so
 * outside every `RiskSmartFormProvider`, which is mounted per-tab inside
 * `FormContext`. React context only flows down, so the toolbar cannot read the
 * active tab's form state directly. This store bridges that gap: the form
 * publishes its save state upward (see `FormContext`, gated on
 * `publishToolbarState`) and the toolbar reads it here.
 *
 * Single-slot by design — Cloudscape `Tabs` mounts only the active tab, so at
 * most one savable form is live at a time. The publishing form clears the slot
 * on unmount (tab switch / navigation), so a non-saving tab leaves
 * `formState === null` and the toolbar greys the save disc.
 */
export interface ObjectToolbarFormState {
  /** The mounted form is editable (not read-only). false → save disc greyed. */
  supported: boolean;
  /** The form has unsaved changes (drives the save disc's attention pulse). */
  dirty: boolean;
  /** A save is in flight. */
  loading: boolean;
  /** Submits the active form — wired to the toolbar's save disc. */
  onSave: () => void;
}

/**
 * Form-editor opener published by the active form's `FormMenu` so the page-header
 * toolbar can launch the form editor (`FormEditorDialog`) from its overflow
 * "Edit" group. Independent of `formState`: a different publisher (`FormMenu`,
 * not `FormContext`) with its own lifecycle, so it has its own setter/clearer
 * and the two slots never clobber each other.
 */
export interface ObjectToolbarFormConfigState {
  /** Opens the active form's editor dialog. */
  openConfigure: () => void;
}

/**
 * Ribbon-editor opener published by a page's `CustomisableRibbon` (relocate mode)
 * so the page-header toolbar can launch the "Edit Filters" modal from its overflow
 * "Edit" group. Like {@link ObjectToolbarFormConfigState} it is a distinct slot
 * with its own setter/clearer — the ribbon owns the modal, the toolbar only holds
 * the opener. Present only when the ribbon's edit button would otherwise show
 * (the user can manage the ribbon and it has finished loading).
 */
export interface ObjectToolbarRibbonConfigState {
  /** Opens the ribbon's "Edit Filters" modal. */
  openEditRibbon: () => void;
}

/**
 * Sidebar-preferences opener published by a page's overview panel (e.g.
 * `RiskOverviewPanel`) so the page-header toolbar can launch the panel's
 * "Edit sidebar preferences" dialog from its overflow "Edit" group. Like the
 * other config slots it is distinct with its own setter/clearer — the panel owns
 * the `PreferencesDialog`, the toolbar only holds the opener. Present only when
 * the panel's own 3-dot would otherwise show (dynamic_forms on + a toolbar
 * mounted). Published by the active tab's panel and cleared on unmount.
 */
export interface ObjectToolbarSidebarPrefsConfigState {
  /** Opens the overview panel's sidebar-preferences dialog. */
  openPreferences: () => void;
}

export interface ObjectToolbarFormStore {
  /** Save state of the active object form, or `null` when none is mounted. */
  formState: ObjectToolbarFormState | null;
  /** Publish (or replace) the active form's save state. */
  setFormState: (state: ObjectToolbarFormState) => void;
  /** Clear the slot — called by the publishing form on unmount. */
  clearFormState: () => void;
  /**
   * Form-editor opener for the active form, or `null` when none is published
   * (no editable FormMenu mounted, or the relocate flags are off).
   */
  formConfigState: ObjectToolbarFormConfigState | null;
  /** Publish (or replace) the active form's editor opener. */
  setFormConfigState: (state: ObjectToolbarFormConfigState) => void;
  /** Clear the slot — called by the publishing FormMenu on unmount. */
  clearFormConfigState: () => void;
  /**
   * Ribbon-editor opener for the current page's customisable ribbon, or `null`
   * when none is published (no ribbon in relocate mode, or the user can't manage
   * it).
   */
  ribbonConfigState: ObjectToolbarRibbonConfigState | null;
  /** Publish (or replace) the ribbon's edit-modal opener. */
  setRibbonConfigState: (state: ObjectToolbarRibbonConfigState) => void;
  /** Clear the slot — called by the publishing ribbon on unmount. */
  clearRibbonConfigState: () => void;
  /**
   * Sidebar-preferences opener for the current page's overview panel, or `null`
   * when none is published (no panel in relocate mode).
   */
  sidebarPrefsConfigState: ObjectToolbarSidebarPrefsConfigState | null;
  /** Publish (or replace) the overview panel's preferences opener. */
  setSidebarPrefsConfigState: (
    state: ObjectToolbarSidebarPrefsConfigState
  ) => void;
  /** Clear the slot — called by the publishing panel on unmount. */
  clearSidebarPrefsConfigState: () => void;
  /**
   * Whether an object toolbar is currently mounted on the page. Set by the
   * connected `ObjectToolbar` on mount/unmount. Publishers (FormMenu, ribbon)
   * consult this so they only relocate their controls into the toolbar when a
   * toolbar actually exists to receive them — the `dynamic_forms` flag can be
   * on globally while a given page renders no toolbar, and relocating there
   * would hide the control entirely (leaving blank space).
   */
  toolbarMounted: boolean;
  /** Set the toolbar-present flag — called by the connected ObjectToolbar. */
  setToolbarMounted: (mounted: boolean) => void;
}

export const useObjectToolbarFormStore = create<ObjectToolbarFormStore>(
  (set) => ({
    formState: null,
    setFormState: (state) => set({ formState: state }),
    clearFormState: () => set({ formState: null }),
    formConfigState: null,
    setFormConfigState: (state) => set({ formConfigState: state }),
    clearFormConfigState: () => set({ formConfigState: null }),
    ribbonConfigState: null,
    setRibbonConfigState: (state) => set({ ribbonConfigState: state }),
    clearRibbonConfigState: () => set({ ribbonConfigState: null }),
    sidebarPrefsConfigState: null,
    setSidebarPrefsConfigState: (state) =>
      set({ sidebarPrefsConfigState: state }),
    clearSidebarPrefsConfigState: () => set({ sidebarPrefsConfigState: null }),
    toolbarMounted: false,
    setToolbarMounted: (mounted) => set({ toolbarMounted: mounted }),
  })
);
